import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/SupabaseAppContext';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTasks, faLightbulb } from '@fortawesome/free-solid-svg-icons';
import { Task } from '../../types';
import FileUpload from '../Attachments/FileUpload';
import AttachmentList from '../Attachments/AttachmentList';

interface TaskFormProps {
  isOpen: boolean;
  onClose: () => void;
  preselectedIdeaId?: string; // Optional: if creating task from an idea
  editingTask?: Task | null; // Optional: if editing an existing task
}

const TaskForm: React.FC<TaskFormProps> = ({ isOpen, onClose, preselectedIdeaId, editingTask }) => {
  const { state, actions } = useApp();
  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    status: 'To Do' | 'In Progress' | 'Done';
    priority: 'High' | 'Medium' | 'Low';
    linkedIdeaId: string;
    assigneeId: string;
    dueDate: string;
  }>({
    title: '',
    description: '',
    status: 'To Do',
    priority: 'Medium',
    linkedIdeaId: preselectedIdeaId || '',
    assigneeId: '',
    dueDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attachmentKey, setAttachmentKey] = useState(0); // Force re-render attachments

  // Initialize form with saved draft when modal opens
  useEffect(() => {
    if (!isOpen) return;
    
    // If editing a task, populate form with task data
    if (editingTask) {
      setFormData({
        title: editingTask.title,
        description: editingTask.description || '',
        status: editingTask.status,
        priority: editingTask.priority,
        linkedIdeaId: editingTask.linkedIdeaId || '',
        assigneeId: editingTask.assignee?.id || '',
        dueDate: editingTask.dueDate ? new Date(editingTask.dueDate).toISOString().split('T')[0] : ''
      });
      return;
    }
    
    // Otherwise, try to load draft for new tasks
    const savedDraft = sessionStorage.getItem('taskFormDraft');
    if (savedDraft) {
      try {
        const draftData = JSON.parse(savedDraft);
        setFormData({
          ...draftData,
          // Preserve preselected idea if provided
          linkedIdeaId: preselectedIdeaId || draftData.linkedIdeaId || ''
        });
      } catch (error) {
        console.error('Error loading task draft:', error);
        // Keep default form state if draft is corrupted
      }
    } else if (preselectedIdeaId) {
      // Only update linkedIdeaId if we have a preselected idea and no draft
      setFormData(prev => ({ ...prev, linkedIdeaId: preselectedIdeaId }));
    }
  }, [isOpen, preselectedIdeaId, editingTask]);

  // Auto-save draft to prevent data loss (only for new tasks)
  useEffect(() => {
    if (!isOpen || editingTask) return;
    
    const hasContent = formData.title.trim() || formData.description.trim();
    if (hasContent) {
      sessionStorage.setItem('taskFormDraft', JSON.stringify(formData));
    }
  }, [formData, isOpen, editingTask]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      setError('Task title is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Find assignee user object if selected
      const assignee = formData.assigneeId 
        ? state.users.find(user => user.id === formData.assigneeId)
        : undefined;

      const taskData = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        status: formData.status,
        priority: formData.priority,
        assignee,
        linkedIdeaId: formData.linkedIdeaId || undefined,
        linkedRoadmapId: undefined, // For now, we'll just link to ideas
        dueDate: formData.dueDate ? new Date(formData.dueDate) : undefined
      };

      if (editingTask) {
        // Update existing task
        const updatedTask = {
          ...editingTask,
          ...taskData
        };
        await actions.updateTask(updatedTask);
      } else {
        // Create new task
        await actions.addTask(taskData);
      }
      
      // Clear draft on successful submission (only for new tasks)
      if (!editingTask) {
        sessionStorage.removeItem('taskFormDraft');
      }
      
      // Reset form and close
      setFormData({
        title: '',
        description: '',
        status: 'To Do',
        priority: 'Medium',
        linkedIdeaId: preselectedIdeaId || '',
        assigneeId: '',
        dueDate: ''
      });
      onClose();
    } catch (error: any) {
      setError(error.message || `Failed to ${editingTask ? 'update' : 'create'} task`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(''); // Clear error when user starts typing
  };

  const handleAttachmentUpload = () => {
    // Refresh attachment list when new files are uploaded
    setAttachmentKey(Date.now());
  };

  // Get the linked idea for display
  const linkedIdea = formData.linkedIdeaId 
    ? state.ideas.find(idea => idea.id === formData.linkedIdeaId)
    : null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <FontAwesomeIcon icon={faTasks} className="mr-3 text-deep-teal" />
            {editingTask ? 'Edit Task' : 'Create New Task'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Task Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Task Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="input-field"
              placeholder="Enter task title..."
              required
            />
          </div>

          {/* Task Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="input-field"
              rows={4}
              placeholder="Describe what needs to be done..."
            />
          </div>

          {/* Link to Idea */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Link to Idea (Optional)
            </label>
            <select
              value={formData.linkedIdeaId}
              onChange={(e) => handleChange('linkedIdeaId', e.target.value)}
              className="input-field"
            >
              <option value="">No linked idea</option>
              {state.ideas.map((idea) => (
                <option key={idea.id} value={idea.id}>
                  {idea.title} ({idea.category})
                </option>
              ))}
            </select>
            {linkedIdea && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center text-sm">
                  <FontAwesomeIcon icon={faLightbulb} className="h-4 w-4 text-blue-600 mr-2" />
                  <span className="font-medium text-blue-900">Linked to:</span>
                  <span className="ml-2 text-blue-700">{linkedIdea.title}</span>
                </div>
              </div>
            )}
          </div>

          {/* Status and Priority Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="input-field"
              >
                <option value="To Do">To Do</option>
                <option value="In Progress">In Progress</option>
                <option value="Done">Done</option>
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => handleChange('priority', e.target.value)}
                className="input-field"
              >
                <option value="Low">🟢 Low</option>
                <option value="Medium">🟡 Medium</option>
                <option value="High">🔴 High</option>
              </select>
            </div>
          </div>

          {/* Assignee and Due Date Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Assignee */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assignee
              </label>
              <select
                value={formData.assigneeId}
                onChange={(e) => handleChange('assigneeId', e.target.value)}
                className="input-field"
              >
                <option value="">Unassigned</option>
                {state.users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.avatar} {user.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Due Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Due Date
              </label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => handleChange('dueDate', e.target.value)}
                className="input-field"
              />
            </div>
          </div>

          {/* File Attachments */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attachments
              </label>
              
              {editingTask?.id ? (
                <FileUpload
                  taskId={editingTask.id}
                  onUploadComplete={handleAttachmentUpload}
                  disabled={loading}
                />
              ) : (
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center bg-gray-50">
                  <p className="text-sm text-gray-500">
                    📎 Attachments can be added after the task is created
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Create your task first, then edit it to add files
                  </p>
                </div>
              )}
            </div>
            
            {/* Show existing attachments */}
            {editingTask?.id && (
              <AttachmentList
                key={attachmentKey}
                taskId={editingTask.id}
                onAttachmentDeleted={handleAttachmentUpload}
              />
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || !formData.title.trim()}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {editingTask ? 'Updating...' : 'Creating...'}
                </div>
              ) : (
                editingTask ? 'Update Task' : 'Create Task'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskForm; 